import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { evaluationApi } from "services/api/aiReviewTest/evaluationApi";
import { queryClient } from "api/queryClient";
// 배럴(CommonUI/index)이 아닌 직접 경로로 import → store→UI 배럴 순환/번들 부작용 회피
import { ToastManager } from "components/organisms/CommonUI/ToastPopup/ToastManager";
import type { RootState } from "store";

interface ReevaluationState {
  running: boolean;
  experimentId: number | null;
  current: number; // 1-based 진행 중 인덱스
  total: number;
}

const initialState: ReevaluationState = {
  running: false,
  experimentId: null,
  current: 0,
  total: 0,
};

// 선택한 평가들을 프론트에서 하나씩(직렬 await) 재평가한다.
// thunk가 루프를 소유하므로 슬라이드 컴포넌트가 언마운트돼도 진행이 계속된다.
export const runReevaluation = createAsyncThunk(
  "reevaluation/run",
  async (
    { experimentId, ids }: { experimentId: number; ids: number[] },
    { dispatch, getState }
  ) => {
    // 단일 진행(single-flight). 이미 진행 중이면 아무 상태도 바꾸지 않고 반환.
    // (finished 미디스패치 → 진행 중 플래그 보존)
    if ((getState() as RootState).reevaluation.running || ids.length === 0) return;

    dispatch(started({ experimentId, total: ids.length }));

    let failed = 0;
    try {
      for (let i = 0; i < ids.length; i++) {
        dispatch(progress(i + 1));
        try {
          const res = await evaluationApi.reEvaluate(ids[i]); // 완료 후 반환 → 직렬
          if (res?.data?.error !== 0) failed++;
        } catch (e) {
          console.error("재평가 실패:", ids[i], e);
          failed++;
        }
      }

      // 슬라이드가 닫혀 있어도 재오픈 시 최신값이 반영되도록 캐시 무효화
      queryClient.invalidateQueries(["experiments", experimentId, "evaluations"]);

      ToastManager.show({
        content:
          failed === 0
            ? `재평가 완료 (${ids.length}건)`
            : `재평가 완료 - 성공 ${ids.length - failed}건 / 실패 ${failed}건`,
        duration: 4000,
      });
    } finally {
      // running=false 는 오직 여기서(정상/예외 모두). 가드-리턴 경로는 여기 도달하지 않는다.
      dispatch(finished());
    }
  }
);

const reevaluationSlice = createSlice({
  name: "reevaluation",
  initialState,
  reducers: {
    started(state, action: PayloadAction<{ experimentId: number; total: number }>) {
      state.running = true;
      state.experimentId = action.payload.experimentId;
      state.current = 0;
      state.total = action.payload.total;
    },
    progress(state, action: PayloadAction<number>) {
      state.current = action.payload;
    },
    finished(state) {
      state.running = false;
    },
  },
});

export const { started, progress, finished } = reevaluationSlice.actions;
export default reevaluationSlice.reducer;
